import "twin.macro";

import { useFormContext, useWatch } from "react-hook-form";

import { Delete } from "@mui/icons-material";
import { Button, IconButton } from "@mui/material";

import { OpaqueCard } from "@components/StyledComponents";
import IndexTable from "@features/ui/indexTable";
import { formatNumber } from "@utility/utilityFunctions";
import {
  ControlledTextInput,
  intValidation,
  moneyAdornment,
  moneyValidation,
} from "@utils/forms";

import { QuoteFormType } from "./QuoteForm";

const QuoteTierPricingForm = () => {
  const {
    control,
    setValue,
    formState: { dirtyFields },
  } = useFormContext();
  const reg = (name, rules?) => ({ control, name, rules });
  const pricingTierQuotes = useWatch<QuoteFormType, "pricingTierQuotes">({
    name: "pricingTierQuotes",
  });

  const addCustomPricingTier = () => {
    setValue("pricingTierQuotes", [
      ...pricingTierQuotes,
      {
        isCustomPricingTier: true,
        id: "",
      },
    ]);
  };

  const handleMarkForDeletion = (idx: number) => {
    setValue(`pricingTierQuotes.${idx}.isMarkedForDeletion`, true, {
      shouldDirty: true,
    });
  };

  const tableRows = Object.values(pricingTierQuotes)
    .map((ptq, idx) => ({ ...ptq, idx }))
    .filter((ptq) => !ptq.isMarkedForDeletion);
  const showDeleteColumn = tableRows.some((ptq) => ptq.isCustomPricingTier);

  const getRequired = (idx: number) => {
    // Require that all fields are filled out if any has been dirtied.
    // This allows requested pricing tiers to be optional.
    return !!dirtyFields.pricingTierQuotes?.[idx] && "Required Field";
  };

  return (
    <OpaqueCard tw="p-0">
      <IndexTable
        rows={tableRows}
        columns={[
          {
            id: "pricingTierQty",
            label: "Qty",
            render: (int, row) =>
              row.isCustomPricingTier ? (
                <ControlledTextInput
                  tw="min-w-[8em]!"
                  {...reg(`pricingTierQuotes.${row.idx}.pricingTierQty`, {
                    ...intValidation,
                    required: getRequired(row.idx),
                  })}
                />
              ) : (
                <div tw="text-base my-2">{formatNumber(int)}</div>
              ),
          },
          {
            id: "cost",
            label: "Cost per Unit *",
            render: (_, row) => (
              <ControlledTextInput
                InputProps={moneyAdornment}
                tw="min-w-[8em]!"
                {...reg(`pricingTierQuotes.${row.idx}.cost`, {
                  ...moneyValidation,
                  required: getRequired(row.idx),
                })}
              />
            ),
          },
          {
            id: "leadTimeInDays",
            label: "Lead Time (days) *",
            render: (_, row) => (
              <ControlledTextInput
                {...reg(`pricingTierQuotes.${row.idx}.leadTimeInDays`, {
                  ...intValidation,
                  required: getRequired(row.idx),
                })}
              />
            ),
          },
          ...(showDeleteColumn
            ? [
                {
                  id: "_delete",
                  label: "",
                  render: (_, row) =>
                    row.isCustomPricingTier && (
                      <IconButton
                        onClick={() => handleMarkForDeletion(row.idx)}
                        tw="-ml-4"
                      >
                        <Delete tw="text-neutral-400" />
                      </IconButton>
                    ),
                },
              ]
            : []),
        ]}
        error={null}
      />
      <div tw="flex justify-end p-2">
        <Button variant="text" color="secondary" onClick={addCustomPricingTier}>
          Add Custom Pricing Tier
        </Button>
      </div>
    </OpaqueCard>
  );
};

export default QuoteTierPricingForm;
